import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  CreditCard as CreditCardIcon,
  Send as SendIcon,
  History as HistoryIcon,
  AccountBalance as AccountBalanceIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  QrCode as QrCodeIcon,
  SupportAgent as SupportAgentIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Download as DownloadIcon,
  VerifiedUser as VerifiedUserIcon,
  Store as StoreIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { logout, clearAuth } from '../../store/slices/authSlice';
import { tokenStore } from '../../services/api';
import { fetchMerchantProfile } from '../../store/slices/merchantSlice';
import { toggleSidebar, setSidebarOpen, addNotification } from '../../store/slices/uiSlice';
import { fetchNotifications, fetchUnreadCount } from '../../store/slices/notificationsSlice';
import { useInactivityTimer } from '../../utils/useInactivityTimer';
import NotificationSnackbar from '../UI/NotificationSnackbar';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Cards', icon: <CreditCardIcon />, path: '/cards' },
  { text: 'Send Money', icon: <SendIcon />, path: '/send-money' },
  { text: 'Transactions', icon: <HistoryIcon />, path: '/transactions' },
  { text: 'Bank Accounts', icon: <AccountBalanceIcon />, path: '/bank-accounts' },
  { text: 'KYC', icon: <VerifiedUserIcon />, path: '/kyc' },
  { text: 'QR Code', icon: <QrCodeIcon />, path: '/qr-code' },
  { text: 'Support', icon: <SupportAgentIcon />, path: '/support' },
];

const merchantMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/merchant/dashboard' },
  { text: 'Payment Links', icon: <LinkIcon />, path: '/merchant/payment-links' },
  { text: 'Store', icon: <StoreIcon />, path: '/merchant/store' },
  { text: 'Transactions', icon: <HistoryIcon />, path: '/merchant/transactions' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/merchant/settings' },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useSelector((state: RootState) => state.auth);
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  const { profile: merchantProfile } = useSelector((state: RootState) => state.merchant);
  const { notifications: serverNotifications } = useSelector((state: RootState) => state.notifications);

  const isMerchantApproved = merchantProfile?.status === 'approved';
  const isMerchantPending = merchantProfile?.status === 'pending';
  const hasMerchantProfile = !!merchantProfile;

  useEffect(() => {
    // Attempt to fetch merchant profile on mount to check status
    dispatch(fetchMerchantProfile());
  }, [dispatch]);

  // --- 60-second inactivity auto-logout ---
  const handleInactivityLogout = useCallback(async () => {
    // Clear tokens immediately so the 401 interceptor can't re-authenticate
    tokenStore.clear();
    try {
      await dispatch(logout()).unwrap();
    } catch {
      // ignore server error — tokens already cleared
    } finally {
      dispatch(clearAuth());
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate]);

  useInactivityTimer(handleInactivityLogout, 60_000);

  // --- Poll for new notifications every 30 seconds ---
  const { unreadCount } = useSelector((state: RootState) => state.notifications);
  const prevUnreadRef = React.useRef(unreadCount);

  useEffect(() => {
    // Initial fetch
    dispatch(fetchUnreadCount());
    dispatch(fetchNotifications({ page: 1, limit: 5 }));

    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
      dispatch(fetchNotifications({ page: 1, limit: 5 }));
    }, 30_000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Request browser notification permission (once per session)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const hasPrompted = sessionStorage.getItem('notif_permission_prompted');
      if (!hasPrompted) {
        // Small delay so the page loads first
        const timer = setTimeout(() => {
          sessionStorage.setItem('notif_permission_prompted', 'true');
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              dispatch(addNotification({
                type: 'success',
                message: 'Browser notifications enabled!',
                duration: 3000,
              }));
            }
          }).catch(() => {
            // Silently handle browsers that block the request
          });
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [dispatch]);

  // Show snackbar + browser notification when new notifications arrive
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
      const diff = unreadCount - prevUnreadRef.current;
      const message = diff === 1
        ? 'You have a new notification'
        : `You have ${diff} new notifications`;
      dispatch(addNotification({
        type: 'info',
        message,
        duration: 5000,
      }));

      // Also show browser notification if permitted
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('MalPay', { body: message, icon: '/favicon.ico' });
        } catch {
          // Silently ignore if browser blocks
        }
      }
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, dispatch]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [merchantMenuOpen, setMerchantMenuOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      dispatch(toggleSidebar());
    }
  };

  const handleLogout = async () => {
    // Clear tokens immediately so the 401 interceptor can't re-authenticate
    tokenStore.clear();
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch(clearAuth());
      navigate('/login', { replace: true });
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
        <Box
          component="img"
          src="https://mal-pay.com/images/logo22.png"
          alt="MalPay"
          sx={{ height: 40, width: 'auto' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="h5" component="div" sx={{
          fontWeight: 800,
          background: 'linear-gradient(135deg, #11C1EC, #09486D)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          MalPay
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1, flex: 1 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 3,
                  py: 1.2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.12) 0%, rgba(9, 72, 109, 0.08) 100%)',
                    color: '#09486D',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.18) 0%, rgba(9, 72, 109, 0.12) 100%)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#11C1EC',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(17, 193, 236, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: isSelected ? '#11C1EC' : '#7F839C',
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.9rem',
                    color: isSelected ? '#09486D' : '#565972',
                  }}
                />
                {isSelected && (
                  <Box sx={{
                    width: 4,
                    height: 24,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #11C1EC, #09486D)',
                    ml: 1,
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}

        {/* Merchant Section */}
        <Divider sx={{ mx: 1, my: 1 }} />
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setMerchantMenuOpen(!merchantMenuOpen)}
            sx={{
              borderRadius: 3,
              py: 1.2,
              backgroundColor: merchantMenuOpen ? 'rgba(17, 193, 236, 0.05)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(17, 193, 236, 0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#7F839C' }}>
              <StoreIcon />
            </ListItemIcon>
            <ListItemText
              primary="Merchant"
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#565972',
              }}
            />
            {merchantMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>

        <Collapse in={merchantMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {merchantMenuItems.map((item) => {
              const isSelected = location.pathname === item.path;
              const isDisabled = !isMerchantApproved;

              const buttonContent = (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={isSelected && !isDisabled}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      navigate(item.path);
                      if (isMobile) {
                        setMobileOpen(false);
                      }
                    }}
                    sx={{
                      borderRadius: 3,
                      py: 1,
                      pl: 1,
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        pointerEvents: 'auto',
                        cursor: 'not-allowed',
                      },
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.12) 0%, rgba(9, 72, 109, 0.08) 100%)',
                        color: '#09486D',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.18) 0%, rgba(9, 72, 109, 0.12) 100%)',
                        },
                        '& .MuiListItemIcon-root': {
                          color: '#11C1EC',
                        },
                      },
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : 'rgba(17, 193, 236, 0.05)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 40,
                      color: isDisabled ? '#B0B3C5' : (isSelected ? '#11C1EC' : '#7F839C'),
                      fontSize: '1.2rem',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isSelected && !isDisabled ? 700 : 500,
                        fontSize: '0.85rem',
                        color: isDisabled ? '#B0B3C5' : (isSelected ? '#09486D' : '#565972'),
                      }}
                    />
                    {isSelected && !isDisabled && (
                      <Box sx={{
                        width: 4,
                        height: 20,
                        borderRadius: 2,
                        background: 'linear-gradient(180deg, #11C1EC, #09486D)',
                        ml: 1,
                      }} />
                    )}
                  </ListItemButton>
                </ListItem>
              );

              return isDisabled ? (
                <Tooltip
                  key={item.text}
                  title="Awaiting verification — apply or wait for admin approval"
                  placement="right"
                  arrow
                >
                  <span>{buttonContent}</span>
                </Tooltip>
              ) : (
                <React.Fragment key={item.text}>{buttonContent}</React.Fragment>
              );
            })}
            {/* Show apply link inside merchant menu if not approved */}
            {!isMerchantApproved && (
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate('/merchant/apply');
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    borderRadius: 3,
                    py: 1,
                    pl: 1,
                    border: '1px dashed rgba(17, 193, 236, 0.4)',
                    '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.05)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: '#11C1EC' }}>
                    <StoreIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={isMerchantPending ? 'View Application' : 'Apply Now'}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: '#11C1EC',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Collapse>
      </List>

      <Divider sx={{ mx: 1 }} />

      {/* Bottom Items */}
      <List sx={{ px: 1, pb: 2 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => {
              navigate('/settings');
              if (isMobile) setMobileOpen(false);
            }}
            selected={location.pathname === '/settings'}
            sx={{
              borderRadius: 3,
              py: 1.2,
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.12) 0%, rgba(9, 72, 109, 0.08) 100%)',
                '& .MuiListItemIcon-root': { color: '#11C1EC' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#7F839C' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem', color: '#565972' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #EDF2FA 0%, #F4F7FC 25%, #F8F8FC 50%, #F0F6FB 75%, #E8F0F9 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        right: 0,
        width: '50vw',
        height: '50vh',
        background: 'radial-gradient(ellipse at 80% 20%, rgba(17, 193, 236, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'fixed',
        bottom: 0,
        left: '20%',
        width: '60vw',
        height: '40vh',
        background: 'radial-gradient(ellipse at 30% 80%, rgba(9, 72, 109, 0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
    }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(32, 38, 71, 0.06)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, color: '#272A3A' }}
          >
            <MenuIcon />
          </IconButton>

          {location.pathname !== '/dashboard' && (
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 1, color: '#565972', '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.08)' } }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography variant="h6" noWrap component="div" sx={{
            flexGrow: 1,
            fontWeight: 700,
            color: '#202647',
          }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'MalPay'}
          </Typography>

          <IconButton
            onClick={() => navigate('/notifications')}
            sx={{
              mr: 1,
              color: '#565972',
              '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.08)' },
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.08)' } }}
          >
            <Avatar
              src={user?.profileImage || undefined}
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                fontSize: '0.9rem',
                fontWeight: 700,
              }}
            >
              {`${user?.firstName?.[0]?.toUpperCase() || ''}${user?.lastName?.[0]?.toUpperCase() || ''}` || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar — Mobile (temporary) */}
      <Box
        component="nav"
        sx={{ width: { md: sidebarOpen ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '1px 0 20px rgba(32, 38, 71, 0.04)',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Sidebar — Desktop (persistent) */}
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              boxShadow: '1px 0 24px rgba(9, 72, 109, 0.06)',
              borderRight: '1px solid rgba(17, 193, 236, 0.06)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 30px rgba(32, 38, 71, 0.12)',
            mt: 1,
            minWidth: 200,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #F0F0F6' }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#202647' }}>
            {user?.firstName || 'User'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.8rem' }}>
            {user?.email || ''}
          </Typography>
        </Box>
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.05)' } }}>
          <AccountCircleIcon sx={{ mr: 1.5, color: '#7F839C', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#565972' }}>Profile</Typography>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.05)' } }}>
          <SettingsIcon sx={{ mr: 1.5, color: '#7F839C', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#565972' }}>Settings</Typography>
        </MenuItem>
        {!isMerchantApproved && (
          <MenuItem onClick={() => { navigate('/merchant/apply'); handleProfileMenuClose(); }}
            sx={{
              py: 1.5,
              '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.05)' },
            }}>
            <StoreIcon sx={{ mr: 1.5, color: '#11C1EC', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#11C1EC', fontWeight: 600 }}>
              {isMerchantPending ? 'Application Status' : 'Merchant/Store Application'}
            </Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}
          sx={{ py: 1.5, '&:hover': { bgcolor: 'rgba(255, 71, 87, 0.05)' } }}>
          <LogoutIcon sx={{ mr: 1.5, color: '#FF4757', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#FF4757' }}>Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 30px rgba(32, 38, 71, 0.12)',
            mt: 1,
            minWidth: 320,
            maxHeight: 400,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #F0F0F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#202647' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography
              variant="caption"
              sx={{ color: '#11C1EC', fontWeight: 600, cursor: 'pointer' }}
              onClick={() => { navigate('/notifications'); handleNotificationMenuClose(); }}
            >
              View all
            </Typography>
          )}
        </Box>
        {serverNotifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ color: '#7F839C' }}>No notifications</Typography>
          </MenuItem>
        ) : (
          serverNotifications.slice(0, 5).map((notif: any) => {
            const isUnread = !notif.is_read;
            const data = notif.data;
            // Build a concise summary for transaction notifications
            let summary = notif.message;
            if (notif.type === 'transaction' && data) {
              const amt = data.amount && data.currency
                ? `${data.currency} ${Number(data.amount).toLocaleString()}`
                : '';
              if (data.type === 'receive') {
                const sender = data.senderName || data.sender || '';
                summary = sender ? `${sender} sent you ${amt}` : `Received ${amt}`;
              } else if (data.type === 'transfer') {
                const recipient = data.recipientName || data.recipient || '';
                summary = recipient ? `Sent ${amt} to ${recipient}` : `Sent ${amt}`;
              }
            }
            // Navigate to transaction detail if available, otherwise notifications page
            const target = (notif.type === 'transaction' && data?.transactionId)
              ? `/transaction/${data.transactionId}`
              : '/notifications';

            return (
              <MenuItem
                key={notif.id}
                onClick={() => { navigate(target); handleNotificationMenuClose(); }}
                sx={{
                  py: 1.5,
                  whiteSpace: 'normal',
                  backgroundColor: isUnread ? 'rgba(17, 193, 236, 0.04)' : 'transparent',
                }}
              >
                <ListItemText
                  primary={notif.title || 'Notification'}
                  secondary={summary || new Date(notif.created_at).toLocaleDateString()}
                  primaryTypographyProps={{ fontSize: '0.85rem', color: '#272A3A', fontWeight: isUnread ? 600 : 400 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem', color: '#7F839C', noWrap: true }}
                />
              </MenuItem>
            );
          })
        )}
      </Menu>

      <NotificationSnackbar />
    </Box>
  );
};

export default Layout;
